import { SPECIAL_ROLES, SPECIAL_ROLE_SETTING_KEYS, type GameSettings } from '@bm/shared';
import { de } from '@/i18n/de';
import { Sheet } from '@/components/Sheet';

interface RulesSheetProps {
  open: boolean;
  onClose: () => void;
  /**
   * Einstellungen der laufenden Runde. Ohne sie (Startseite) werden die
   * Sonderrollen weggelassen — dort ist noch nicht entschieden, welche gelten.
   */
  settings?: GameSettings;
}

/**
 * Kurzerklärung für alle, die zum ersten Mal mitspielen.
 *
 * Bewusst kein vollständiges Regelwerk: Die App ist der Spielleiter, sie führt
 * ohnehin durch jede Phase. Hier steht nur, was man wissen muss, bevor die
 * erste Abstimmung läuft — und welche Sonderrollen in *dieser* Runde im Spiel
 * sind, denn die ändern den Ablauf spürbar.
 */
export function RulesSheet({ open, onClose, settings }: RulesSheetProps) {
  const activeSpecials = settings
    ? SPECIAL_ROLES.filter((role) => settings.specialRoles[SPECIAL_ROLE_SETTING_KEYS[role]])
    : [];

  return (
    <Sheet open={open} onClose={onClose} title={de.rules.title}>
      <div className="flex flex-col gap-6">
        <section>
          <h3 className="mb-2 font-display font-semibold">{de.rules.goalTitle}</h3>
          <ul className="flex flex-col gap-2 text-sm">
            <li className="rounded-xl border border-citizen/40 bg-citizen-muted px-3 py-2 text-fg">
              {de.rules.goalCitizens}
            </li>
            <li className="rounded-xl border border-mafia/40 bg-mafia-muted px-3 py-2 text-fg">
              {de.rules.goalMafia}
            </li>
          </ul>
        </section>

        <section>
          <h3 className="mb-2 font-display font-semibold">{de.rules.flowTitle}</h3>
          <ol className="flex flex-col gap-2 text-sm text-fg-muted">
            {de.rules.flowSteps.map((step, index) => (
              <li key={step} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-2 font-display text-xs font-bold text-fg">
                  {index + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </section>

        {settings && (
          <section>
            <h3 className="mb-2 font-display font-semibold">{de.rules.rolesTitle}</h3>
            {activeSpecials.length === 0 ? (
              <p className="text-sm text-fg-muted">{de.rules.rolesNone}</p>
            ) : (
              <ul className="flex flex-col gap-2 text-sm">
                {activeSpecials.map((role) => (
                  <li key={role} className="rounded-xl border border-surface-border bg-bg px-3 py-2">
                    <span className="font-medium text-fg">
                      {de.specialRoles[role].emoji} {de.roles[role]}
                    </span>
                    <span className="mt-0.5 block text-fg-muted">
                      {de.specialRoles[role].lobbyHint}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </Sheet>
  );
}
